/**
 * RequireAuth — V9.9
 *
 * Wrapper de route qui force l'authentification.
 *
 * Comportement :
 *  - Si user connecté → rend les enfants normalement.
 *  - Si user non connecté → redirige vers /connexion?next=<location.pathname>.
 *  - Affiche un état "Chargement" pendant la résolution initiale de l'auth
 *    (évite un flash de redirection avant que /api/auth/me ne réponde).
 *
 * Usage :
 *   <Route path="/creer-ma-fiche" element={
 *     <RequireAuth><CreateProfileWizard /></RequireAuth>
 *   } />
 */
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";

export default function RequireAuth({ children, allowRoles = null }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="require-auth-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/connexion?next=${next}`} replace />;
  }

  // Restriction par rôle (optionnel) : ex. <RequireAuth allowRoles={["admin","copf"]}>
  if (allowRoles && Array.isArray(allowRoles) && allowRoles.length > 0) {
    const userRoles = user.roles || [];
    if (!userRoles.some((r) => allowRoles.includes(r))) {
      return (
        <div className="mx-auto max-w-md p-8 text-center" data-testid="require-auth-forbidden">
          <h2 className="font-display text-xl font-bold">Accès refusé</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Cette page est réservée aux rôles : {allowRoles.join(", ")}.
          </p>
        </div>
      );
    }
  }

  return children;
}
